import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import logo from "@/assets/logo.png";

export function routeForUser(user) {
  if (!user) return "/login";
  if (user.role === "admin") return "/admin";
  if (!user.access_active) return "/checkout";
  if (!user.onboarding_complete) return "/onboarding";
  return "/home";
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const user = await login(email, password);
      toast.success("Welcome back");
      navigate(routeForUser(user));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-app flex flex-col" data-testid="login-page">
      <div className="max-w-md w-full mx-auto px-6 py-10 flex-1 flex flex-col justify-center">
        <Link to="/" className="mb-8 flex justify-center" data-testid="login-logo">
          <img src={logo} alt="Meno Flames and Finesse" className="h-28 w-auto" />
        </Link>
        <div className="bg-white rounded-3xl card-shadow p-8">
          <h1 className="font-serif text-3xl font-bold text-ink">Welcome back</h1>
          <p className="text-ink-soft text-sm mt-2">Log in to continue your reset.</p>

          <form onSubmit={submit} className="mt-7 space-y-4">
            <div>
              <label className="text-sm font-semibold text-ink">Email</label>
              <input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1.5 w-full rounded-xl border border-subtle bg-app px-4 py-3 text-ink outline-none focus:border-[var(--brand)] transition-colors"
                placeholder="you@email.com"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-ink">Password</label>
              <input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1.5 w-full rounded-xl border border-subtle bg-app px-4 py-3 text-ink outline-none focus:border-[var(--brand)] transition-colors"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-3">
                {error}
              </div>
            )}
            <button
              data-testid="login-submit"
              disabled={busy}
              className="w-full bg-brand hover:bg-[var(--brand-hover)] text-white font-semibold py-3.5 rounded-full transition-colors disabled:opacity-60"
            >
              {busy ? "Logging in…" : "Log in"}
            </button>
          </form>

          <div className="flex items-center justify-between mt-5 text-sm">
            <Link to="/forgot-password" className="text-ink-soft hover:text-ink" data-testid="forgot-link">
              Forgot password?
            </Link>
            <Link to="/checkout" className="text-brand font-semibold" data-testid="to-checkout-link">
              Get access · £47.99/yr
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
